import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import ComposerBody from 'flarum/forum/components/ComposerBody';
import TextEditor from 'flarum/common/components/TextEditor';
import Button from 'flarum/common/components/Button';

import contextFor, { hasTitle } from './context';
import { loadDraft, saveDraft, deleteDraft } from './api';
import { loadLocalDraft, saveLocalDraft, deleteLocalDraft } from './storage';

app.initializers.add('happydigital-draft-post', () => {
  /**
   * Replace the editor's visible content.
   *
   * The editor's DOM is managed imperatively by its driver, outside of
   * Mithril's diffing, so pushing a new value into the `content` stream alone
   * never changes what the user sees. `insertBetween` (the documented way to
   * mutate editor content) over the whole existing range is the only way to
   * make the visible text match.
   */
  function setEditorContent(composer, value) {
    const previousLength = (composer.fields.content() || '').length;

    composer.fields.content(value);

    if (composer.editor) {
      composer.editor.insertBetween(0, previousLength, value);
    } else {
      // Browser-local drafts resolve immediately and can beat the editor's
      // `oncreate` hook on some redraw paths. Apply the same replacement once
      // the driver has been attached.
      setTimeout(() => {
        if (composer.editor && composer.fields.content() === value) {
          composer.editor.insertBetween(0, previousLength, value);
          m.redraw();
        }
      });
    }
  }

  /**
   * Read the composer's current title, if this context has one.
   */
  function currentTitle(composer, context) {
    if (!hasTitle(context) || typeof composer.fields.title !== 'function') {
      return null;
    }

    return composer.fields.title() || '';
  }

  function currentContent(composer) {
    return composer.fields.content() || '';
  }

  function usesSimpleStorage(composer) {
    return composer.draftStorageMode === 'simple';
  }

  function loadComposerDraft(composer, context) {
    if (usesSimpleStorage(composer)) {
      return Promise.resolve().then(() => loadLocalDraft(app.session.user.id()));
    }

    return loadDraft(context);
  }

  function saveComposerDraft(composer, context, title, content) {
    if (usesSimpleStorage(composer)) {
      return Promise.resolve().then(() => saveLocalDraft(app.session.user.id(), title, content));
    }

    return saveDraft(context, title, content);
  }

  function deleteComposerDraft(composer, context) {
    if (usesSimpleStorage(composer)) {
      return Promise.resolve().then(() => deleteLocalDraft(app.session.user.id()));
    }

    return deleteDraft(context);
  }

  /**
   * Attach draft handling when the composer opens, and restore what was saved.
   *
   * Anything the user has already typed wins: restoring over live input would
   * destroy work rather than protect it.
   */
  extend(ComposerBody.prototype, 'oninit', function () {
    const context = contextFor(this);

    if (!context || !app.session.user || !app.forum.attribute('canSaveDrafts')) return;

    // Stored on the composer state, not the component: `TextEditor` (where the
    // save button actually lives) shares this object but not the component
    // instance.
    const composer = this.composer;
    composer.draftContext = context;
    composer.draftSaving = false;
    composer.draftExists = false;
    composer.draftStorageMode = app.forum.attribute('draftPostStorageMode') === 'simple' ? 'simple' : 'advanced';

    // If the user types while an advanced draft is travelling over the
    // network, their newer text wins. An untouched initial value (including an
    // existing post's original body) can safely be replaced by its saved draft.
    const initialContent = currentContent(composer);

    loadComposerDraft(composer, context)
      .then((draft) => {
        if (!draft) return;

        composer.draftExists = true;

        if (draft.content && currentContent(composer) === initialContent) {
          setEditorContent(composer, draft.content);
        }

        if (hasTitle(context) && draft.title && typeof composer.fields.title === 'function' && !composer.fields.title()) {
          composer.fields.title(draft.title);
        }

        m.redraw();
      })
      .catch(() => {
        // Failing to restore a draft must not block writing a new post.
      });
  });

  /**
   * Add the "Save draft" button immediately to the left of the submit button.
   *
   * The submit button lives in `TextEditor.controlItems`, keyed `'submit'` at
   * default priority — not in `ComposerBody.headerItems`, which only holds the
   * title/context row above the editor. A higher priority places this one
   * before it, in the same control row and using the same `Button` component,
   * so it picks up identical styling for free.
   */
  extend(TextEditor.prototype, 'controlItems', function (items) {
    const composer = this.attrs.composer;
    const context = composer && composer.draftContext;

    if (!context) return;

    if (composer.draftExists) {
      // Taslak kaydedilmişse: sadece sil butonu göster
      items.add(
        'draft-post-delete',
        Button.component(
          {
            className: 'Button DraftPost-deleteButton',
            itemClassName: 'App-primaryControl',
            icon: composer.draftSaving ? 'fas fa-spinner fa-spin' : 'fas fa-trash-alt',
            disabled: composer.draftSaving,
            onclick: () => {
              composer.draftSaving = true;

              deleteComposerDraft(composer, context)
                .then(() => {
                  composer.draftSaving = false;
                  composer.draftExists = false;

                  app.alerts.show({ type: 'success' }, app.translator.trans('happydigital-draft-post.forum.draft_deleted'));
                  m.redraw();
                })
                .catch((error) => {
                  composer.draftSaving = false;
                  m.redraw();

                  throw error;
                });
            },
          },
          app.translator.trans('happydigital-draft-post.forum.delete_draft')
        ),
        5
      );
    } else {
      // Taslak kaydedilmemişse: sadece kaydet butonu göster
      items.add(
        'draft-post-save',
        Button.component(
          {
            className: 'Button DraftPost-saveButton',
            itemClassName: 'App-primaryControl',
            icon: composer.draftSaving ? 'fas fa-spinner fa-spin' : 'fas fa-file-alt',
            disabled: composer.draftSaving,
            onclick: () => {
              const content = currentContent(composer);
              const title = currentTitle(composer, context);

              if (!content && !title) {
                app.alerts.show({ type: 'error' }, app.translator.trans('happydigital-draft-post.forum.empty_draft'));
                return;
              }

              composer.draftSaving = true;

              saveComposerDraft(composer, context, title, content)
                .then(() => {
                  composer.draftSaving = false;
                  composer.draftExists = true;

                  app.alerts.show({ type: 'success' }, app.translator.trans('happydigital-draft-post.forum.draft_saved'));

                  m.redraw();
                })
                .catch((error) => {
                  composer.draftSaving = false;
                  m.redraw();

                  throw error;
                });
            },
          },
          app.translator.trans('happydigital-draft-post.forum.save_draft')
        ),
        5
      );
    }
  });

  /**
   * Clear the draft once the post actually goes through.
   *
   * A submission that fails leaves the composer open so the user can retry, so
   * `onsubmit` alone is not proof of success. `close()` (the user dismissing
   * the composer by hand) also calls `hide()`, so the wrapper below is scoped
   * to fire only once and only for the submission that installed it.
   */
  extend(TextEditor.prototype, 'onsubmit', function () {
    const composer = this.attrs.composer;
    const context = composer && composer.draftContext;

    if (!context || composer.draftOriginalHide) return;

    const originalHide = composer.hide;
    composer.draftOriginalHide = originalHide;

    composer.hide = function (...args) {
      composer.hide = originalHide;
      delete composer.draftOriginalHide;

      deleteComposerDraft(composer, context).catch(() => {
        // The post itself succeeded; a leftover draft is not worth an error.
      });

      return originalHide.apply(composer, args);
    };
  });

  // Core calls `loaded` when submission fails. In that case the composer stays
  // open and its draft must remain, so remove the success-only hide wrapper.
  extend(ComposerBody.prototype, 'loaded', function () {
    const composer = this.composer;

    if (!composer || !composer.draftOriginalHide) return;

    composer.hide = composer.draftOriginalHide;
    delete composer.draftOriginalHide;
  });

  /**
   * Fix layout: ensure draft buttons sit inline (side by side) with the
   * submit button. CSS cannot reliably override Flarum's core list-item
   * styles, so we apply inline styles directly on the <li> elements.
   *
   * NOTE: Flarum's `extend` passes (returnValue, ...originalArgs) to the
   * callback, so we must NOT rely on the first parameter being the vnode.
   * Instead we use `this.element`, which Flarum's Component base class sets
   * to the root DOM node during oncreate/onupdate.
   */
  function fixDraftButtonLayout(root) {
    if (!root) return;

    ['li.item-draft-post-delete', 'li.item-draft-post-save'].forEach((sel) => {
      const el = root.querySelector(sel);
      if (el) {
        el.style.setProperty('display', 'inline-block');
        el.style.setProperty('vertical-align', 'middle');
        el.style.setProperty('margin-right', '40px', 'important');
        el.style.setProperty('top', '3px', 'important');
      }
    });
  }

  // `this.element` is reliably set by Flarum's Component base class
  // before our callback runs, so it always points to the real DOM root.
  extend(TextEditor.prototype, 'oncreate', function () {
    fixDraftButtonLayout(this.element);
  });

  extend(TextEditor.prototype, 'onupdate', function () {
    fixDraftButtonLayout(this.element);
  });
});
